import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { differenceInDays } from 'date-fns/differenceInDays';
import { CompanyData, UserCompany } from '../../../interfaces';
import { AuthService } from '../../../services/auth.service';
import { BILLING_STATUS } from '../../../enums';
import { Router } from '@angular/router';
import { iif, of } from 'rxjs';

@Component({
  selector: 'app-company-card-subscription',
  templateUrl: './company-card-subscription.component.html',
  styleUrl: './company-card-subscription.component.scss'
})
export class CompanyCardSubscriptionComponent {
  private readonly TRIAL_WARNING_LIMIT_DAYS = 3;
  protected readonly BILLING_STATUS = BILLING_STATUS;
  @Input({ required: true }) company!: CompanyData | UserCompany;
  @Input() isTrial: boolean = false;

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private dialogService: MatDialog,
    private router: Router,
  ) {}

  warningMessage() {
    const isExpiredTrial = this.isTrial && this.company.billing?.status === BILLING_STATUS.PAST_DUE;

    if (isExpiredTrial) {
      return this.translateService.instant('subscription.warning.trialEnded');
    }

    if (this.company.billing?.status === BILLING_STATUS.PAST_DUE) {
      return this.translateService.instant('subscription.warning.pastDue');
    }

    if (this.company.billing?.status === BILLING_STATUS.UNSUBSCRIBED) {
      return this.translateService.instant('subscription.warning.unsubscribed');
    }

    if (this.company.billing?.status === BILLING_STATUS.SUSPENDED) {
      return this.translateService.instant('subscription.warning.readOnly');
    }
  }

  shouldShowTimer(): boolean {
    return this.company.billing?.status === BILLING_STATUS.FREE_TRIAL || this.company.billing?.status === BILLING_STATUS.PAST_DUE;
  }

  shouldShowWarning(): boolean {
    return (
      this.company.billing?.status === BILLING_STATUS.SUSPENDED ||
      this.company.billing?.status === BILLING_STATUS.UNSUBSCRIBED ||
      this.company.billing?.status === BILLING_STATUS.PAST_DUE
    );
  }

  trialNearEnd(): boolean {
    return (
      this.company.billing?.status === BILLING_STATUS.FREE_TRIAL &&
      differenceInDays(new Date(this.company.billing.trialEndAt), new Date()) <= this.TRIAL_WARNING_LIMIT_DAYS
    );
  }

  async selectPlan(): Promise<void> {
    const { ChangePlanModalComponent } = await import('../../modals/subscription/change-plan-modal/change-plan-modal.component');

    iif(
      () => String(this.company.id) === this.authService.getCurrentCompanyId(),
      this.authService.employeeSignIn(String(this.company.id)),
      of(null)
    ).subscribe({
      next: () => {
        this.dialogService.open(ChangePlanModalComponent, {
          panelClass: ['dialog-overlay-pane'],
          width: '590px',
          data: {
            companyId: this.company.id,
            discount: this.company.billing?.discountPercentage
          }
        });
      }
    });

  }

  toSubscriptionPage(): void {
    this.router.navigate(['/company', this.company.id, 'subscription']).catch();
  }
}
